import axios from "axios";
import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { IoClose } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "../../redux/userSlice";
import ImageViewer from "../ImageViewer";
import "../formStyles.css"; // Import the general form styles
import useUploadFiles from "../useUploadFiles";

function ProfileForm() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);

  const fileInputRef = useRef(null);
  const [fileExists, setFileExists] = useState(false);
  const [isGenderValid, setIsGenderValid] = useState(true);
  const [isAchievementValid, setIsAchievementValid] = useState(true);
  const [isBioValid, setIsBioValid] = useState(true);

  const { user, token } = useSelector((state) => state.auth);
  const role = user?.role; // Directly retrieve role

  const { uploadFiles } = useUploadFiles(); // Use the custom hook
  const [username, setUserName] = useState(user.username || "");
  const [profilePicture, setProfilePicture] = useState(
    user.profilePicture || ""
  );
  const [currentProfilePicture, setCurrentProfilePicture] = useState(
    user.profilePicture || ""
  );
  const [bio, setBio] = useState(user.bio || "");
  const [achievement, setAchievement] = useState(user.achievement || ""); // State for achievements
  const [gender, setGender] = useState(user.gender || "");

  // // Populate form fields with existing user data
  // useEffect(() => {
  //   console.log(user);
  //   if (user === null) {
  //     console.log("logout ", user);
  //     // Navigate to the login page if the user is null
  //     navigate("/login");
  //     return null;
  //   }
  //   const userId = user.userId;
  //   const fetchUserData = async () => {
  //     try {
  //       const response = await axios.get(
  //         `${process.env.REACT_APP_BACKEND_URL}/user/${userId}`,
  //         {
  //           headers: {
  //             Authorization: `Bearer ${token}`,
  //           },
  //         }
  //       );
  //       const userData = response.data.data;
  //       setUserName(userData.username || "");
  //       setProfilePicture(userData.profilePicture || "");
  //       setCurrentProfilePicture(userData.profilePicture || "");
  //       setBio(userData.bio || "");
  //       setGender(userData.gender || "");
  //       setAchievement(userData.achievement || ""); // Set existing achievement
  //     } catch (err) {
  //       console.error("Error fetching user data:", err);
  //       toast.error(t("fetchError")); // Notify user of the error
  //     }
  //   };

  //   if (userId) {
  //     fetchUserData();
  //   }
  // }, [user, token, t, navigate]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0]; // Get the first selected file
    if (selectedFile) {
      setProfilePicture(selectedFile);
      setFileExists(true); // Update state to indicate a file is selected
    } else {
      setFileExists(false); // No file selected
    }
  };

  const validateForm = () => {
    if (!username) {
      return t("register.username") + t("register.required");
    }
    if (username.length < 3) {
      return t("register.usernameMinLength");
    }
    if (username.length > 20) {
      return t("register.usernameMaxLength");
    }
    if (!gender) {
      setIsGenderValid(false);
      return t("register.gender") + t("register.required");
    } else {
      setIsGenderValid(true);
    }

    if (role === "tutor") {
      if (!bio) {
        setIsBioValid(false);
        return t("register.Bio") + t("register.required");
      }
      if (!achievement) {
        setIsAchievementValid(false);
        return t("register.achievement") + t("register.required");
      }
      if (bio.length < 10) {
        setIsBioValid(false);
        return t("register.bioMinLength");
      } else {
        setIsBioValid(true);
      }
      if (bio.length > 1000) {
        setIsBioValid(false);
        return t("register.bioMaxLength");
      }
      if (achievement && achievement.length < 10) {
        setIsAchievementValid(false);
        return t("register.achievementMinLength");
      } else {
        setIsAchievementValid(true);
      }
      if (achievement && achievement.length > 1000) {
        setIsBioValid(false);
        return t("register.achievementMaxLength");
      }
    }
    return null; // No errors
  };

  // Handle Update Profile
  const handleUpdateProfile = async (event) => {
    event.preventDefault(); // Prevent default form submission behavior

    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError); // Show error toast
      return; // Stop form submission if there's an error
    }
    setStatus("loading");

    try {
      let uploadedFiles;
      if (fileExists) {
        const sourceType = "profilePicture"; // Example source type
        const uploaded = await uploadFiles([profilePicture], sourceType);
        console.log(uploaded);
        uploadedFiles = JSON.parse(uploaded.names)[0]["filePath"];
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/users.php`,
        {
          id: user.id,
          username,
          bio,
          gender,
          profilePicture: uploadedFiles ? uploadedFiles : profilePicture, // Attach uploaded file names to the request
          achievement, // Include achievements in the request
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          withCredentials: true, // ensure cookies are sent if the backend expects them
        }
      );
      const userData = response.data.user;
      dispatch(setUser(userData));

      toast.success(t("updateSuccess"));
      setStatus("succeeded");
    } catch (error) {
      console.error("Error updating profile:", error);
      setError(error.response?.data?.message || error.message); // Extract error message if available
      toast.error(t("updateFailed"));
      setStatus("failed");
    }
  };

  if (status === "loading") {
    return <div>{t("loading")}</div>; // Provide a loading message
  }

  if (status === "failed") {
    return (
      <div>
        {t("error")}: {error}
      </div>
    );
  }

  const clearFileInput = () => {
    setProfilePicture(null); // Clear the selected file state
    setProfilePicture(currentProfilePicture);
    setCurrentProfilePicture(null);
    setFileExists(false); // No file selected
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Clear the input value
    }
  };

  return (
    <div className="form-container">
      <h2>{t("updateProfile")}</h2>{" "}
      <div className="grid grid-cols-1 md:grid-cols  m-1">
        <div className="  m-1">
          <label>
            {t("register.email")}: {user.email}
          </label>
          <label>
            {t("register.username")}:
            <input
              type="text"
              value={username}
              onChange={(e) => setUserName(e.target.value)}
              required
            />
          </label>
        </div>
        <div className={` m-1 ${!isGenderValid ? "required-field" : ""}`}>
          <label>
            {t("register.gender")}:
            <div className="form-selector">
              <span
                onClick={() => setGender("male")}
                className={`form-option ${gender === "male" ? "selected" : ""}`}
              >
                {t("register.male")}
              </span>
              <span
                onClick={() => setGender("female")}
                className={`form-option ${
                  gender === "female" ? "selected" : ""
                }`}
              >
                {t("register.female")}
              </span>
            </div>
          </label>
        </div>
        <div className="  m-1">
          <div className="relative ">
            <label>
              {t("register.profilePicture")}:
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*" // Accept image files only
                onChange={handleFileChange} // Set the selected file
                // Limit file size to 2MB
                style={{ maxWidth: "250px" }}
              />
              <div style={{ fontSize: "0.85em", color: "gray" }}>
                {t("limitedSize")}: 2MB
              </div>
            </label>
            {!(typeof profilePicture === "string") && (
              <div
                onClick={clearFileInput}
                className="absolute top-1/2 pt-1 right-2 transform -translate-y-1/2 bg-gray-200 border border-gray-300 rounded-md px-3 py-1 text-sm text-gray-700 cursor-pointer hover:bg-gray-300"
              >
                <IoClose />
              </div>
            )}
          </div>
          {/* Display the current profile picture, fixed div size */}
          <div className="flex justify-center p-2 ">
            <div className="w-28 h-28 ">
              {profilePicture && <ImageViewer imageName={profilePicture} />}
            </div>
          </div>
        </div>
        <div className=" m-1">
          <label>
            {t("register.Bio")}:
            <textarea
              className={`  ${!isBioValid ? "required-field" : ""}`}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder={t("register.enterBio")}
              rows={10}
            />
            <div
              style={{
                fontSize: "0.85em",
                color: bio.length < 10 || bio.length > 1000 ? "red" : "gray",
              }}
            >
              {bio.length}
              /1000
              <span style={{ marginLeft: 8, color: "gray" }}>
                {bio.length < 10
                  ? t("register.bioMinLength")
                  : bio.length > 1000
                  ? t("register.bioMaxLength")
                  : ""}
              </span>
            </div>
          </label>
          {role === "tutor" && (
            <label>
              {t("register.achievement")}:
              <textarea
                className={`  ${!isAchievementValid ? "required-field" : ""}`}
                value={achievement}
                onChange={(e) => setAchievement(e.target.value)}
                placeholder={t("register.enterAchievement")}
                rows={6}
              />
              <div
                style={{
                  fontSize: "0.85em",
                  color:
                    achievement.length < 10 || achievement.length > 1000
                      ? "red"
                      : "gray",
                }}
              >
                {achievement.length}/1000
                <span style={{ marginLeft: 8, color: "gray" }}>
                  {achievement.length < 10
                    ? t("register.achievementMinLength")
                    : achievement.length > 1000
                    ? t("register.achievementMaxLength")
                    : ""}
                </span>
              </div>
            </label>
          )}
        </div>
        <div>
          <button type="button" onClick={handleUpdateProfile}>
            {t("updateProfile")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProfileForm;
